import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { api } from "../../lib/api";

export default function Prospects() {
  const [prospects, setProspects] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [campaigns, setCampaigns] = React.useState([]);
  const [emailLogs, setEmailLogs] = React.useState([]);
  const [message, setMessage] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [prospectSearch, setProspectSearch] = React.useState("");
  const [countryFilter, setCountryFilter] = React.useState("all");
  const [languageFilter, setLanguageFilter] = React.useState("all");
  const [contactFilter, setContactFilter] = React.useState("all");
  const [prospectSort, setProspectSort] = React.useState("country");
  const [emailLogSearch, setEmailLogSearch] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [country, setCountry] = React.useState("FR");
  const [template, setTemplate] = React.useState({
    id: "",
    groupKey: "",
    language: "fr",
    name: "",
    subject: "",
    body: "",
  });
  const [previewGroupKey, setPreviewGroupKey] = React.useState("");
  const [previewLanguage, setPreviewLanguage] = React.useState("fr");
  const [bounceLog, setBounceLog] = React.useState({ open: false, campaign: null, rows: [], loading: false });
  const [bounceChecking, setBounceChecking] = React.useState(false);
  const emptyCampaign = {
    name: "",
    mode: "global",
    targetCountry: "",
    templateId: "",
    templateIdsByCountry: {},
    ratePerHour: 20,
    cooldownDays: 7,
  };
  const [campaign, setCampaign] = React.useState(emptyCampaign);
  const fileRef = React.useRef(null);
  const templateFileRef = React.useRef(null);
  const languages = [
    { code: "fr", label: "Français" },
    { code: "it", label: "Italien" },
    { code: "de", label: "Allemand" },
    { code: "en", label: "Anglais" },
    { code: "es", label: "Espagnol" },
    { code: "pl", label: "Polonais" },
    { code: "pt", label: "Portugais" },
    { code: "cs", label: "Tchèque" },
    { code: "nl", label: "Néerlandais" },
  ];
  const countryCodes = [
    { code: "FR", country: "France", language: "Français" },
    { code: "BE", country: "Belgique", language: "Français" },
    { code: "LU", country: "Luxembourg", language: "Français" },
    { code: "IT", country: "Italie", language: "Italien" },
    { code: "DE", country: "Allemagne", language: "Allemand" },
    { code: "ES", country: "Espagne", language: "Espagnol" },
    { code: "PL", country: "Pologne", language: "Polonais" },
    { code: "PT", country: "Portugal", language: "Portugais" },
    { code: "GB", country: "Royaume-Uni", language: "Anglais" },
    { code: "CZ", country: "République tchèque", language: "Tchèque" },
    { code: "NL", country: "Pays-Bas", language: "Néerlandais" },
  ];
  const languageName = (code) =>
    languages.find((item) => item.code === code)?.label ||
    String(code || "fr").toUpperCase();
  const availableCountries = React.useMemo(
    () =>
      Array.from(
        new Set(prospects.map((item) => item.country).filter(Boolean)),
      ).sort(),
    [prospects],
  );
  const campaignCountries = React.useMemo(() => {
    const withEmail = prospects.filter((item) => item.email);
    const codes = Array.from(
      new Set(withEmail.map((item) => item.country).filter(Boolean)),
    ).sort();
    return withEmail.some((item) => !item.country)
      ? [...codes, "DEFAULT"]
      : codes;
  }, [prospects]);
  const countryStats = React.useMemo(
    () =>
      availableCountries.map((code) => ({
        code,
        count: prospects.filter((item) => item.country === code).length,
      })),
    [availableCountries, prospects],
  );
  const contactStats = React.useMemo(() => {
    const rows =
      countryFilter === "all"
        ? prospects
        : prospects.filter((item) => item.country === countryFilter);
    return {
      email: rows.filter((item) => item.email).length,
      phone: rows.filter((item) => item.phone).length,
      both: rows.filter((item) => item.email && item.phone).length,
      emailOnly: rows.filter((item) => item.email && !item.phone).length,
      phoneOnly: rows.filter((item) => item.phone && !item.email).length,
    };
  }, [prospects, countryFilter]);
  const filteredProspects = React.useMemo(() => {
    const query = prospectSearch.trim().toLowerCase();
    const rows = prospects.filter((item) => {
      if (countryFilter !== "all" && item.country !== countryFilter)
        return false;
      if (
        languageFilter !== "all" &&
        (item.language || "fr") !== languageFilter
      )
        return false;
      if (contactFilter === "email" && !item.email) return false;
      if (contactFilter === "phone" && !item.phone) return false;
      if (contactFilter === "both" && (!item.email || !item.phone))
        return false;
      if (contactFilter === "emailOnly" && (!item.email || item.phone))
        return false;
      if (contactFilter === "phoneOnly" && (!item.phone || item.email))
        return false;
      if (!query) return true;
      return [
        item.email,
        item.phone,
        item.country,
        item.language,
        item.source,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );
    });
    return [...rows].sort((a, b) => {
      if (prospectSort === "recent")
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (prospectSort === "email")
        return (
          Number(!a.email) - Number(!b.email) ||
          String(a.email || "").localeCompare(String(b.email || ""), "fr")
        );
      if (prospectSort === "phone")
        return (
          Number(!a.phone) - Number(!b.phone) ||
          String(a.phone || "").localeCompare(String(b.phone || ""), "fr")
        );
      if (prospectSort === "language")
        return String(a.language || "fr").localeCompare(
          String(b.language || "fr"),
          "fr",
        );
      return (
        String(a.country || "ZZ").localeCompare(
          String(b.country || "ZZ"),
          "fr",
        ) || String(a.email || "").localeCompare(String(b.email || ""), "fr")
      );
    });
  }, [
    prospects,
    prospectSearch,
    countryFilter,
    languageFilter,
    contactFilter,
    prospectSort,
  ]);
  const filteredEmailLogs = React.useMemo(() => {
    const query = emailLogSearch.trim().toLowerCase();
    if (!query) return emailLogs;
    return emailLogs.filter((item) =>
      [
        item.prospect?.email,
        item.prospect?.country,
        item.campaign?.name,
        item.emailSubject,
        item.status,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [emailLogs, emailLogSearch]);
  const formatDateTime = (value) =>
    value
      ? new Intl.DateTimeFormat("fr-FR", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(value))
      : "—";
  const templateGroups = React.useMemo(() => {
    const groups = new Map();
    templates.forEach((item) => {
      const key = item.groupKey || item.id;
      if (!groups.has(key))
        groups.set(key, { groupKey: key, name: item.name, versions: [] });
      groups.get(key).versions.push(item);
    });
    return Array.from(groups.values());
  }, [templates]);
  const templateLabel = (item) =>
    `${item.name} · ${languageName(item.language || "fr")} (${String(item.language || "fr").toUpperCase()})`;
  const previewGroup = templateGroups.find(
    (group) => group.groupKey === previewGroupKey,
  );
  const previewTemplate =
    previewGroup?.versions.find((item) => item.language === previewLanguage) ||
    previewGroup?.versions[0];

  const load = React.useCallback(async () => {
    try {
      const [p, t, c, logs] = await Promise.all([
        api.get("/prospects"),
        api.get("/prospects/templates"),
        api.get("/prospects/campaigns"),
        api.get("/prospects/email-logs"),
      ]);
      setProspects(p.data || []);
      setTemplates(t.data || []);
      setCampaigns(c.data || []);
      setEmailLogs(logs.data || []);
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Chargement impossible.",
      });
    }
  }, []);
  React.useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/prospects/import", form);
      setMessage({
        type: "success",
        text: `${data.imported} prospect(s) importé(s), ${data.duplicates} doublon(s) détecté(s), ${data.invalid} ligne(s) invalide(s). Le pays des doublons est actualisé si nécessaire.`,
      });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Import impossible.",
      });
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };
  const addProspect = async () => {
    try {
      await api.post("/prospects", { email, phone, country });
      setAddOpen(false);
      setEmail("");
      setPhone("");
      setCountry("FR");
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Ajout impossible.",
      });
    }
  };
  const saveTemplate = async () => {
    try {
      await api.post("/prospects/templates", template);
      setTemplate({
        id: "",
        groupKey: "",
        language: "fr",
        name: "",
        subject: "",
        body: "",
      });
      setMessage({ type: "success", text: "Template enregistré." });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Enregistrement impossible.",
      });
    }
  };
  const createCampaign = async () => {
    try {
      const { data } = await api.post("/prospects/campaigns", campaign);
      setCampaign(emptyCampaign);
      setMessage({
        type: "success",
        text: `Campagne créée avec ${data.totalCount} destinataire(s). ${data.skippedRecentCount || 0} contact(s) récemment contacté(s) et ${data.skippedReservedCount || 0} contact(s) déjà prévu(s) dans une autre campagne ont été ignorés.`,
      });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Création impossible.",
      });
    }
  };
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/prospects/campaigns/${id}/status`, { status });
      await load();
    } catch (e) {
      setMessage({ type: "error", text: e?.response?.data?.message || "Modification impossible." });
    }
  };
  const deleteCampaign = async (item) => {
    if (!window.confirm(`Supprimer la campagne « ${item.name} » ?`)) return;
    try {
      await api.delete(`/prospects/campaigns/${item.id}`);
      setMessage({ type: "success", text: "Campagne supprimée." });
      await load();
    } catch (e) {
      setMessage({ type: "error", text: e?.response?.data?.message || "Suppression impossible." });
    }
  };
  const exportProspects = async (includeInactive = false) => {
    const response = await api.get(includeInactive ? "/prospects/export-all.xlsx" : "/prospects/export.xlsx", {
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = includeInactive ? "prospects-123back-archive.xlsx" : "prospects-123back-actifs.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };
  const openBounceLog = async (item) => {
    setBounceLog({ open: true, campaign: item, rows: [], loading: true });
    try {
      const { data } = await api.get(`/prospects/campaigns/${item.id}/bounces`);
      setBounceLog({ open: true, campaign: item, rows: data || [], loading: false });
    } catch (e) {
      setBounceLog({ open: true, campaign: item, rows: [], loading: false });
      setMessage({ type: "error", text: e?.response?.data?.message || "Journal des retours impossible à charger." });
    }
  };
  const checkBouncesNow = async () => {
    setBounceChecking(true);
    try {
      const { data } = await api.post('/prospects/bounce-monitor/check');
      if (data?.error) {
        setMessage({ type: 'error', text: `Vérification de la boîte impossible : ${data.error}` });
      } else {
        setMessage({
          type: 'success',
          text: `Boîte vérifiée : ${data?.scanned || 0} nouveau(x) message(s), ${data?.detected || 0} rejet(s) définitif(s), ${data?.deactivated || 0} prospect(s) désactivé(s).`,
        });
        await load();
      }
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Vérification de la boîte impossible.' });
    } finally {
      setBounceChecking(false);
    }
  };
  const importTemplate = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = await file.text();
    setTemplate((current) => ({
      ...current,
      name: current.name || file.name.replace(/\.[^.]+$/, ""),
      body,
    }));
    event.target.value = "";
  };
  const editTemplate = (item) => {
    setTemplate({
      id: item.id,
      groupKey: item.groupKey,
      language: item.language || "fr",
      name: item.name,
      subject: item.subject,
      body: item.body,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const addTranslation = (group) => {
    const source =
      group.versions.find((item) => item.language === "fr") ||
      group.versions[0];
    const missing = languages.find(
      (language) =>
        !group.versions.some((item) => item.language === language.code),
    );
    setTemplate({
      id: "",
      groupKey: group.groupKey,
      language: missing?.code || "it",
      name: source.name,
      subject: "",
      body: "",
    });
  };
  const openPreview = (group) => {
    setPreviewGroupKey(group.groupKey);
    setPreviewLanguage(group.versions[0]?.language || "fr");
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Prospects
            </Typography>
            <Typography color="text.secondary">
              Import, dédoublonnage, export et campagnes e-mail progressives.
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <input
              hidden
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={importFile}
            />
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => fileRef.current?.click()}
            >
              Importer XLSX/CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
            >
              Ajouter
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => exportProspects(false)}
            >
              Exporter les actifs
            </Button>
            <Button
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={() => exportProspects(true)}
            >
              Archive complète
            </Button>
            <Button
              variant="outlined"
              disabled={bounceChecking}
              onClick={checkBouncesNow}
            >
              {bounceChecking ? "Vérification…" : "Vérifier les retours"}
            </Button>
          </Stack>
        </Stack>
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 2.5,
            bgcolor: "#f4f8fd",
            border: "1px solid #d7e6f7",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="primary" fontSize="small" />
            <Typography fontWeight={800}>Préparer le fichier Excel</Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            Utilise les colonnes <strong>email</strong>, <strong>tel</strong> et{" "}
            <strong>pays</strong>. Dans la colonne pays, indique le code en
            majuscules :
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            {countryCodes.map((item) => (
              <Chip
                key={item.code}
                label={`${item.code} · ${item.country} · ${item.language}`}
                size="small"
                variant="outlined"
                sx={{ bgcolor: "#fff" }}
              />
            ))}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Pour l’Allemagne, utilise de préférence <strong>DE</strong>. Le code
            GE reste accepté et sera automatiquement converti en DE. Sans pays
            reconnu, le français est utilisé par défaut.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          {[
            [
              "1",
              "Recherche",
              "Trouve des réparateurs hors Paris via Google Maps, Facebook ou TikTok.",
            ],
            [
              "2",
              "Ajout",
              "Ajoute le contact à la main ou dans Excel avec son code pays.",
            ],
            [
              "3",
              "Contact",
              "Filtre par pays, puis contacte les prospects avec le bon message.",
            ],
            [
              "4",
              "Campagne",
              "Choisis un template traduit et démarre l’envoi progressif.",
            ],
          ].map(([number, title, description]) => (
            <Box
              key={number}
              sx={{
                flex: 1,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={number} size="small" color="primary" />
                <Typography fontWeight={800}>{title}</Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                {description}
              </Typography>
            </Box>
          ))}
        </Stack>
        {busy && <LinearProgress sx={{ mt: 2 }} />}{" "}
        {message && (
          <Alert
            severity={message.type}
            sx={{ mt: 2 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}
        <Box sx={{ mt: 2.5 }}>
          <Typography fontWeight={800}>Répartition des prospects</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            1. Choisis le pays
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
            <Chip
              label={`${prospects.length} · Tous les pays`}
              variant={countryFilter === "all" ? "filled" : "outlined"}
              color={countryFilter === "all" ? "primary" : "default"}
              onClick={() => setCountryFilter("all")}
            />
            {countryStats.map((item) => (
              <Chip
                key={item.code}
                label={`${item.code} · ${item.count}`}
                variant={countryFilter === item.code ? "filled" : "outlined"}
                color={countryFilter === item.code ? "primary" : "default"}
                onClick={() => setCountryFilter(item.code)}
              />
            ))}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            2. Choisis le type de coordonnées
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
            <Chip
              label="Tous"
              variant={contactFilter === "all" ? "filled" : "outlined"}
              color={contactFilter === "all" ? "primary" : "default"}
              onClick={() => setContactFilter("all")}
            />
            <Chip
              label={`${contactStats.email} · E-mail`}
              variant={contactFilter === "email" ? "filled" : "outlined"}
              color={contactFilter === "email" ? "primary" : "default"}
              onClick={() => setContactFilter("email")}
            />
            <Chip
              label={`${contactStats.phone} · Téléphone`}
              variant={contactFilter === "phone" ? "filled" : "outlined"}
              color={contactFilter === "phone" ? "primary" : "default"}
              onClick={() => setContactFilter("phone")}
            />
            <Chip
              label={`${contactStats.both} · E-mail + téléphone`}
              variant={contactFilter === "both" ? "filled" : "outlined"}
              color={contactFilter === "both" ? "primary" : "default"}
              onClick={() => setContactFilter("both")}
            />
            <Chip
              label={`${contactStats.emailOnly} · E-mail uniquement`}
              variant={contactFilter === "emailOnly" ? "filled" : "outlined"}
              color={contactFilter === "emailOnly" ? "primary" : "default"}
              onClick={() => setContactFilter("emailOnly")}
            />
            <Chip
              label={`${contactStats.phoneOnly} · Téléphone uniquement`}
              variant={contactFilter === "phoneOnly" ? "filled" : "outlined"}
              color={contactFilter === "phoneOnly" ? "primary" : "default"}
              onClick={() => setContactFilter("phoneOnly")}
            />
          </Stack>
        </Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <TextField
            size="small"
            fullWidth
            label="Rechercher un e-mail ou un téléphone…"
            value={prospectSearch}
            onChange={(e) => setProspectSearch(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Pays"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">Tous les pays</MenuItem>
            {availableCountries.map((code) => (
              <MenuItem key={code} value={code}>
                {code} ·{" "}
                {countryCodes.find((item) => item.code === code)?.country ||
                  code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Langue"
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">Toutes les langues</MenuItem>
            {languages.map((item) => (
              <MenuItem key={item.code} value={item.code}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Trier par"
            value={prospectSort}
            onChange={(e) => setProspectSort(e.target.value)}
            sx={{ minWidth: 175 }}
          >
            <MenuItem value="country">Pays</MenuItem>
            <MenuItem value="language">Langue</MenuItem>
            <MenuItem value="email">E-mail</MenuItem>
            <MenuItem value="phone">Téléphone</MenuItem>
            <MenuItem value="recent">Plus récents</MenuItem>
          </TextField>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 1.5 }}
        >
          <Typography fontWeight={700}>
            {filteredProspects.length} prospect(s) affiché(s)
          </Typography>
          {(prospectSearch ||
            countryFilter !== "all" ||
            languageFilter !== "all" ||
            contactFilter !== "all") && (
            <Button
              size="small"
              onClick={() => {
                setProspectSearch("");
                setCountryFilter("all");
                setLanguageFilter("all");
                setContactFilter("all");
              }}
            >
              Réinitialiser les filtres
            </Button>
          )}
        </Stack>
        <TableContainer
          sx={{
            maxHeight: 420,
            mt: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>E-mail</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Pays</TableCell>
                <TableCell>Langue d’envoi</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProspects.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.email || "—"}</TableCell>
                  <TableCell>{p.phone || "—"}</TableCell>
                  <TableCell>
                    {p.country ? (
                      <Chip size="small" label={p.country} variant="outlined" />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={languageName(p.language || "fr")}
                      sx={{
                        bgcolor: "#eaf3fd",
                        color: "#0358ab",
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell>{p.source || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {template.id
                ? "Modifier le template"
                : template.groupKey
                  ? "Ajouter une traduction"
                  : "Nouveau template e-mail"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crée une version par langue. Variables disponibles : {"{{email}}"}{" "}
              et {"{{phone}}"}.
            </Typography>
          </Box>
          <input
            hidden
            ref={templateFileRef}
            type="file"
            accept=".txt,.html,.htm"
            onChange={importTemplate}
          />
          <Button
            startIcon={<UploadFileIcon />}
            onClick={() => templateFileRef.current?.click()}
          >
            Importer
          </Button>
        </Stack>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              label="Langue"
              value={template.language}
              onChange={(e) =>
                setTemplate({ ...template, language: e.target.value })
              }
              sx={{ minWidth: 180 }}
            >
              {languages.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Nom du template"
              value={template.name}
              onChange={(e) =>
                setTemplate({ ...template, name: e.target.value })
              }
            />
          </Stack>
          <TextField
            label="Objet"
            value={template.subject}
            onChange={(e) =>
              setTemplate({ ...template, subject: e.target.value })
            }
          />
          <TextField
            multiline
            minRows={6}
            label="Message"
            value={template.body}
            onChange={(e) => setTemplate({ ...template, body: e.target.value })}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={saveTemplate}
              disabled={!template.name || !template.subject || !template.body}
            >
              Enregistrer
            </Button>
            {(template.id || template.groupKey) && (
              <Button
                onClick={() =>
                  setTemplate({
                    id: "",
                    groupKey: "",
                    language: "fr",
                    name: "",
                    subject: "",
                    body: "",
                  })
                }
              >
                Annuler
              </Button>
            )}
          </Stack>
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={800}>
          Templates enregistrés
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {templateGroups.length === 0 && (
            <Typography color="text.secondary">Aucun template.</Typography>
          )}
          {templateGroups.map((group) => (
            <Paper key={group.groupKey} variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={1}
              >
                <Box>
                  <Typography fontWeight={700}>{group.name}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                    {group.versions.map((item) => (
                      <Chip
                        key={item.id}
                        size="small"
                        label={String(item.language || "fr").toUpperCase()}
                      />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => openPreview(group)}>
                    Visualiser
                  </Button>
                  <Button
                    size="small"
                    onClick={() => editTemplate(group.versions[0])}
                  >
                    Modifier
                  </Button>
                  <Button size="small" onClick={() => addTranslation(group)}>
                    Traduire
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" gap={1} alignItems="center">
          <CampaignIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Campagnes et suivi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choisis une campagne globale avec un template par pays, ou cible
              uniquement un pays avec un seul template.
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={2} sx={{ my: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Nom de la campagne"
              value={campaign.name}
              onChange={(e) =>
                setCampaign({ ...campaign, name: e.target.value })
              }
              fullWidth
            />
            <TextField
              select
              label="Type de campagne"
              value={campaign.mode}
              onChange={(e) =>
                setCampaign({
                  ...campaign,
                  mode: e.target.value,
                  targetCountry: "",
                  templateId: "",
                })
              }
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="global">Globale · tous les pays</MenuItem>
              <MenuItem value="country">Ciblée · un seul pays</MenuItem>
            </TextField>
          </Stack>
          {campaign.mode === "global" ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#f7f9fc",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography fontWeight={800}>
                Template à envoyer pour chaque pays
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tous les pays contenant des prospects avec une adresse e-mail
                doivent avoir un template.
              </Typography>
              <Stack
                direction={{ xs: "column", md: "row" }}
                gap={2}
                flexWrap="wrap"
              >
                {campaignCountries.map((code) => (
                  <TextField
                    key={code}
                    select
                    label={
                      code === "DEFAULT"
                        ? "Sans pays"
                        : `${code} · ${countryCodes.find((item) => item.code === code)?.country || code}`
                    }
                    value={campaign.templateIdsByCountry[code] || ""}
                    onChange={(e) =>
                      setCampaign({
                        ...campaign,
                        templateIdsByCountry: {
                          ...campaign.templateIdsByCountry,
                          [code]: e.target.value,
                        },
                      })
                    }
                    sx={{ minWidth: 290, flex: 1 }}
                  >
                    {templates.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {templateLabel(item)}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Stack>
            </Box>
          ) : (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                label="Pays ciblé"
                value={campaign.targetCountry}
                onChange={(e) =>
                  setCampaign({ ...campaign, targetCountry: e.target.value })
                }
                sx={{ minWidth: 250 }}
              >
                {availableCountries.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code} ·{" "}
                    {countryCodes.find((item) => item.code === code)?.country ||
                      code}{" "}
                    (
                    {
                      prospects.filter(
                        (item) => item.country === code && item.email,
                      ).length
                    }{" "}
                    e-mail(s))
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Template à envoyer"
                value={campaign.templateId}
                onChange={(e) =>
                  setCampaign({ ...campaign, templateId: e.target.value })
                }
                fullWidth
              >
                {templates.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {templateLabel(item)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "flex-start" }}
          >
            <TextField
              type="number"
              label="Maximum / heure"
              value={campaign.ratePerHour}
              inputProps={{ min: 1, max: 300 }}
              onChange={(e) =>
                setCampaign({ ...campaign, ratePerHour: e.target.value })
              }
            />
            <TextField
              type="number"
              label="Ne pas relancer avant"
              value={campaign.cooldownDays}
              inputProps={{ min: 0, max: 365 }}
              onChange={(e) =>
                setCampaign({ ...campaign, cooldownDays: e.target.value })
              }
              helperText="Nombre de jours (7 conseillé)"
            />
            <Button
              variant="contained"
              size="large"
              onClick={createCampaign}
              disabled={
                campaign.mode === "global"
                  ? !campaignCountries.length ||
                    campaignCountries.some(
                      (code) => !campaign.templateIdsByCountry[code],
                    )
                  : !campaign.targetCountry || !campaign.templateId
              }
            >
              Créer la campagne
            </Button>
          </Stack>
        </Stack>
        <Alert severity="info" sx={{ mb: 2 }}>
          La campagne globale utilise exactement le template choisi pour chaque
          pays. La campagne ciblée envoie uniquement au pays sélectionné. Le
          délai anti-relance reste appliqué dans les deux cas.
        </Alert>
        <Divider />
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {campaigns.length === 0 && (
            <Typography color="text.secondary">Aucune campagne.</Typography>
          )}
          {campaigns.slice(0, 10).map((c) => {
            const progress = c.totalCount
              ? Math.round(((c.sentCount + c.failedCount) / c.totalCount) * 100)
              : 0;
            return (
              <Paper key={c.id} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  gap={1}
                >
                  <Box>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography fontWeight={800}>{c.name}</Typography>
                      <Chip size="small" label={c.status} />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          c.mode === "country"
                            ? `Ciblée · ${c.targetCountry}`
                            : "Globale"
                        }
                      />
                    </Stack>
                    <Typography variant="body2">
                      {c.sentCount} envoyé(s) · {c.failedCount} échec(s) ·{" "}
                      {c.totalCount} destinataire(s) · limite {c.ratePerHour}/h
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Délai anti-relance : {c.cooldownDays ?? 7} jour(s) ·{" "}
                      {c.skippedRecentCount || 0} contact(s) récent(s) ignoré(s)
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button variant="text" onClick={() => openBounceLog(c)}>
                      Retours ({c.bouncedCount || 0})
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setStatus(
                          c.id,
                          c.status === "running" ? "paused" : "running",
                        )
                      }
                      disabled={c.status === "completed"}
                    >
                      {c.status === "running" ? "Mettre en pause" : "Démarrer"}
                    </Button>
                    {['draft', 'paused'].includes(c.status) && (
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => deleteCampaign(c)}
                      >
                        Supprimer
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ mt: 1 }}
                />
              </Paper>
            );
          })}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Historique des e-mails
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ce journal est conservé sur le site. Il permet de savoir qui a été
            contacté, quand, dans quelle langue et avec quelle campagne.
          </Typography>
        </Box>
        <TextField
          size="small"
          fullWidth
          label="Rechercher dans l’historique…"
          value={emailLogSearch}
          onChange={(e) => setEmailLogSearch(e.target.value)}
          sx={{ mt: 2 }}
        />
        <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
          {filteredEmailLogs.length} envoi(s) affiché(s)
        </Typography>
        <TableContainer
          sx={{
            maxHeight: 420,
            mt: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Pays</TableCell>
                <TableCell>Langue</TableCell>
                <TableCell>Campagne</TableCell>
                <TableCell>Objet</TableCell>
                <TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmailLogs.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDateTime(item.sentAt || item.updatedAt)}
                  </TableCell>
                  <TableCell>{item.prospect?.email || "—"}</TableCell>
                  <TableCell>{item.prospect?.country || "—"}</TableCell>
                  <TableCell>
                    {languageName(
                      item.emailLanguage || item.prospect?.language || "fr",
                    )}
                  </TableCell>
                  <TableCell>{item.campaign?.name || "—"}</TableCell>
                  <TableCell>{item.emailSubject || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.status === "sent" ? "Envoyé" : "Échec"}
                      color={item.status === "sent" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Ajouter un prospect</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="E-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Téléphone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <TextField
              label="Code pays"
              value={country}
              onChange={(e) =>
                setCountry(e.target.value.toUpperCase().slice(0, 2))
              }
              helperText="Exemples : FR, BE, IT, DE"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={addProspect}
            disabled={!email && !phone}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(previewGroupKey)}
        onClose={() => setPreviewGroupKey("")}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Aperçu du template</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Langue"
            value={previewTemplate?.language || previewLanguage}
            onChange={(e) => setPreviewLanguage(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          >
            {previewGroup?.versions.map((item) => (
              <MenuItem key={item.id} value={item.language}>
                {languages.find((language) => language.code === item.language)
                  ?.label || item.language.toUpperCase()}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="overline">Objet</Typography>
          <Typography fontWeight={800} sx={{ mb: 2 }}>
            {previewTemplate?.subject}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, whiteSpace: "pre-wrap" }}>
            {previewTemplate?.body}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (previewTemplate) editTemplate(previewTemplate);
              setPreviewGroupKey("");
            }}
          >
            Modifier cette version
          </Button>
          <Button variant="contained" onClick={() => setPreviewGroupKey("")}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={bounceLog.open}
        onClose={() => setBounceLog({ open: false, campaign: null, rows: [], loading: false })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Adresses retirées · {bounceLog.campaign?.name || "Campagne"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ces adresses ont généré un échec définitif de remise. Elles restent dans l’archive complète, mais sont exclues des prochaines campagnes.
          </Typography>
          {bounceLog.loading ? (
            <LinearProgress />
          ) : bounceLog.rows.length === 0 ? (
            <Alert severity="success">Aucune adresse retirée pour cette campagne.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Motif</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bounceLog.rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.reason || "Échec définitif"}</TableCell>
                      <TableCell>{row.detectedAt ? new Date(row.detectedAt).toLocaleString("fr-FR") : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setBounceLog({ open: false, campaign: null, rows: [], loading: false })}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
